/* eslint-disable no-underscore-dangle */
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

import Chip from '@shared/atoms/Chip';
import CircularSpinner from '@shared/atoms/CircularSpinner';
import HorizontalRule from '@shared/atoms/HorizontalRule';
import SearchField from '@shared/atoms/SearchField';
import Snackbar from '@shared/atoms/Snackbar';
import T from '@shared/atoms/Typography';
import Table, { CELL_TYPES, ORDER_TYPES } from '@shared/organisms/Table';
import theme from '@shared/themes/default';
import { useAppGlobals } from '../../contexts/AppContext';

import UsersTableRowItem from './components/UsersTableRowItem';
import { listUsers } from './graphql/queries';

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m1};
`;

const UsersView = () => {
  const { useLazyGraphQLQuery } = useAppGlobals();
  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState('');
  const [hasError, setHasError] = useState(false);
  const [hasSuccess, setHasSuccess] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [users, setUsers] = useState([]);

  const [listUsersQuery, listUsersResponse] = useLazyGraphQLQuery(listUsers, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const { loading, data, error } = listUsersResponse;

    if (!loading && (data || error)) {
      if (error) {
        setErrorMessage('Failed to load users');
        setHasError(true);
      } else if (data) {
        setUsers(
          data.listUsers?.map((user) => {
            if (user.__typename === 'User') {
              return user;
            }
            return {
              ...user,
              id: `${user.id}#${user.__typename}`,
            };
          })
        );
      }
    }
  }, [listUsersResponse]);

  const debouncedCall = useCallback(
    debounce((value) => {
      listUsersQuery({
        variables: {
          pagination: {
            limit: 1000,
          },
          filter: {
            contains: value,
            includeInvitations: true,
          },
        },
      });
    }, 400),
    []
  );

  useEffect(() => {
    if (searchValue && searchValue.length > 2) {
      debouncedCall(searchValue);
    } else {
      setSelectedItemIds([]);
      setUsers([]);
    }
  }, [searchValue]);

  const toggleShowExpandedUser = (id) => {
    if (selectedItemIds.includes(id)) {
      setSelectedItemIds((items) => items.filter((itemId) => id !== itemId));
    } else {
      setSelectedItemIds((items) => [...items, id]);
    }
  };

  const onDeleteUser = (id) => {
    setSelectedItemIds((items) => items.filter((itemId) => id !== itemId));
    setUsers([...users.filter((user) => user.id !== id)]);
  };

  return (
    <OuterWrapper>
      <T fontWeight="medium" variant="headingS">
        {t('backoffice.users.users')}
      </T>
      <HorizontalRule />
      <SearchField
        ariaLabel={t('backoffice.users.search')}
        onChange={({ value }) => setSearchValue(value)}
        onClear={() => setSearchValue('')}
        placeholder={t('backoffice.users.search')}
        loading={listUsersResponse.loading}
        theme="dark"
        type="primary"
        value={searchValue}
      />
      {users.length === 0 && listUsersResponse.loading && <CircularSpinner />}
      {users.length > 0 && (
        <Table
          ariaLabel="Users table"
          columns={[
            {
              key: 'name',
              label: t('backoffice.users.name'),
              type: CELL_TYPES.COMPONENT,
              render: (row) => (
                <>
                  {row.name}
                  {row.__typename === 'Invitation' && (
                    <Chip
                      label={t('backoffice.users.notRegistered')}
                      small
                      sx={{ marginLeft: theme.spacing.m1 }}
                      theme="dark"
                    />
                  )}
                </>
              ),
            },
            {
              key: 'email',
              label: t('backoffice.users.email'),
            },
          ]}
          items={users}
          keyField="id"
          defaultOrder={ORDER_TYPES.ASC}
          defaultOrderBy="name"
          rowIsClickable={() => true}
          selectedItemIds={selectedItemIds}
          onRowClick={(row) => toggleShowExpandedUser(row.id)}
          ExpandedItemComponent={UsersTableRowItem}
          expandedItemComponentProps={{
            onDeleteUser,
            setErrorMessage,
            setHasError,
            setHasSuccess,
            setSuccessMessage,
          }}
        />
      )}
      {users.length === 0 && !listUsersResponse.loading && (
        <T fontWeight="medium" variant="subtitle">
          {t('backoffice.users.noUsersFound')}
        </T>
      )}
      <Snackbar
        type="success"
        message={successMessage}
        open={hasSuccess}
        onClose={() => setHasSuccess(false)}
      />
      <Snackbar
        type="error"
        message={errorMessage}
        open={hasError}
        onClose={() => setHasError(false)}
      />
    </OuterWrapper>
  );
};

export default UsersView;
